import { IBaseResource } from "../api.types";
import { ICarrierMinimal } from "../carriers/carriers.types.ts";
import { AIModelType, IClauseChecklistMinimal } from "../clause-checklists";
import {
    IClauseMinimal,
    IInheritedClauseGuidance,
    IOrganizationClauseGuidance,
} from "../clauses";
import { IClauseComment } from "../comments/comments.types.ts";
import {
    FileType,
    IExtractedFieldResponse,
} from "../documents/documents.types";
import { IIndustry } from "../industries/industries.types";
import { IOrganizationMinimal } from "../organizations";
import { IUser, IUserMinimal } from "../users/users.types.ts";

export enum ScopeStatus {
    PENDING = "pending",
    PROCESSING = "processing",
    COMPLETE = "complete",
    FAILED = "failed",
}

export interface IDocument {
    id: number;
    document_name: string;
    file_type?: FileType | null;
    carrier?: ICarrierMinimal;
    page_count?: number;
}

export interface IScopeStatus {
    status: ScopeStatus;
}

export interface IScopeMinimal {
    id: number;
    document_title: string;
    clause_checklist_id: number;
    organization_id: number;
    client?: string;
    model_type?: AIModelType;
}

export interface IHighlightArea {
    left: number;
    top: number;
    width: number;
    height: number;
    page_index: number;
}

export interface IScopeClauseClause extends IClauseMinimal {
    organization_clause_guidance?: IOrganizationClauseGuidance | null;
    inherited_guidance?: IInheritedClauseGuidance | null;
}

export interface IScopeClauseMetadata {
    reasoning: string | null;
    confidence: number | null;
}

export interface IScopeClause extends IBaseResource {
    scope_id: number;
    found: boolean;
    content: string | null;
    highlight_areas: IHighlightArea[];
    clause: IScopeClauseClause;
    scope: IScopeMinimal;
    guidance?: string | null;
    guidance_created_by?: IUserMinimal | null;
    comments: IClauseComment[];
    meta?: IScopeClauseMetadata | null;
    page_number?: number | null;
}

export interface IScopeClauseMinimal {
    id: number;
    clause: IClauseMinimal;
}

export interface IScopeBase {
    organization_id: number;
    document_title: string;
    model_type: AIModelType;
}

export interface IScopeCreate extends Omit<IScopeBase, "model_type"> {
    clause_checklist_id: number;
    document_file?: string;
    document_id?: number;
    field_extraction_enabled?: boolean;
    industry_id?: number;
    /** Client tag at birth (Unit 3.4); omitted for the global flow. */
    client_id?: number;
}

export interface IScopeUpdate extends Omit<
    IScopeBase,
    "organization_id" | "model_type"
> {}

export interface IScopeTopic {
    id: number;
    topic_id: number | null;
    name: string;
    clauses_analyzed: number;
    clauses_found: number;
    fields_extracted: number;
}

export interface IScope extends IScopeBase, IBaseResource {
    clause_checklist_id: number;
    user_id: number;
    clause_checklist: IClauseChecklistMinimal;
    user: IUser;
    organization: IOrganizationMinimal;
    client?: string;
    status: ScopeStatus;
    field_extraction_enabled: boolean;
    show_gaps: boolean;
    processing_completed_at?: string;
    job_started_at?: string;
    job_completed_at?: string;
    document: IDocument;
    scope_topics: IScopeTopic[];
    is_v2: boolean;
    industry_id?: number | null;
    industry?: IIndustry | null;
}

export interface ISignedScopeDocument {
    signed_url: string;
}

export enum BaseExportType {
    XLSX = "xlsx",
}

export interface IExportBase {
    clauses: number[];
    include_guidance: boolean;
    include_comments: boolean;
    include_metadata: boolean;
    include_fields: boolean;
    export_type: BaseExportType;
}

export enum PolicyType {
    COMMERCIAL_AUTO = "Commercial Auto",
    COMMERCIAL_GENERAL_LIABILITY = "Commercial General Liability",
    COMMERCIAL_PROPERTY = "Commercial Property",
    CRIME = "Crime",
    EXCESS_OR_UMBRELLA = "Excess or Umbrella",
    INLAND_MARINE = "Inland Marine",
    WORKERS_COMPENSATION = "Worker's Compensation",
}

export interface IScopeReportRequest {
    clauses: number[];
    include_guidance?: boolean;
    include_comments?: boolean;
    include_metadata?: boolean;
}

export enum ResourceType {
    PROJECT = "project",
    SCOPE = "scope",
}

export interface ICoverageOption {
    name: string;
    value?: boolean;
}

export interface ICoverageLimit {
    name: string;
    value?: string | boolean;
}

export interface ICoverage {
    type: string;
    insurer_letter?: string;
    coverage_options?: ICoverageOption[];
    additional_insured?: boolean;
    subrogation_waived?: boolean;
    policy_number?: string;
    policy_effective_date?: string;
    policy_expiration_date?: string;
    limits?: ICoverageLimit[];
}

export interface IExtractedCertificateOfInsurance {
    page_num: number;
    certificate_number: string;
    certificate_date?: string;
    revision_number?: string;
    authorized_representative?: string;

    producer_name?: string;
    producer_address?: string;
    producer_contact_name?: string;
    producer_phone?: string;
    producer_fax?: string;
    producer_email?: string;

    insured_name?: string;
    insured_address?: string;

    certificate_holder_name?: string;
    certificate_holder_address?: string;

    insurer_a_name?: string;
    insurer_a_naic?: string;
    insurer_b_name?: string;
    insurer_b_naic?: string;
    insurer_c_name?: string;
    insurer_c_naic?: string;
    insurer_d_name?: string;
    insurer_d_naic?: string;
    insurer_e_name?: string;
    insurer_e_naic?: string;
    insurer_f_name?: string;
    insurer_f_naic?: string;

    coverages: ICoverage[];

    description_of_operations?: string;
}

// V2 Topics Types

export interface IDocumentSectionTypeMinimal {
    id: number;
    name: string;
}

export interface IScopeTopicField {
    id: number;
    field_name: string;
    value: string | null;
    confidence: number | null;
    page_number: number | null;
    datatype_name: string | null;
}

export interface IScopeTopicSection {
    section_type: IDocumentSectionTypeMinimal | null;
    clauses: IScopeClause[];
}

export interface IScopeTopicWithClauses {
    id: number;
    topic_id: number | null;
    name: string;
    clauses_analyzed: number;
    clauses_found: number;
    fields_extracted: number;
    sections: IScopeTopicSection[];
}

export interface IScopeTopicsWithClauses {
    scope_id: number;
    topics: IScopeTopicWithClauses[];
}

export type PropertySource = "extracted" | "custom";

/**
 * Extended field type that tracks whether a field originated from a custom field.
 * When _customFieldId is set, the field supports delete-field and add-property UIs.
 */
export interface IExtractedFieldWithMeta extends IExtractedFieldResponse {
    _customFieldId?: number;
}

export interface IExtractedPropertyUpdate {
    value: string | null;
    page_number?: number | null;
}

export interface IBatchDeleteRequest {
    field_definition_id: number;
    array_index: number;
    expected_delete_fingerprint: string;
}

export interface IBatchDeleteResponse {
    deleted_count: number;
}

// Scope Overview Types (for Overview tab)

export interface ITopField {
    property_id: number;
    field_name: string;
    field_value: string | null;
    policy_type: string;
}

export interface IGapClause {
    clause_id: number;
    clause_title: string;
    clause_description: string | null;
    document_section_types: string | null;
    priority: number | null;
}

export interface ITopicOverviewCard {
    id: number;
    name: string;
    clauses_found: number;
    clauses_analyzed: number;
    summary: string | null;
    top_fields: ITopField[];
    gaps: IGapClause[];
}

export interface IScopeOverview {
    scope_id: number;
    headline: string | null;
    topics: ITopicOverviewCard[];
    show_gaps: boolean;
    clauses_analyzed: number;
    clauses_found: number;
}
